import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Chart from 'react-apexcharts';
import { motion } from 'framer-motion';
import { FaSearch, FaLightbulb, FaCalculator, FaChartLine } from 'react-icons/fa';

const RelativisticDopplerCalculator = () => {
  const [sourceFrequency, setSourceFrequency] = useState('');
  const [observerVelocity, setObserverVelocity] = useState('');
  const [sourceVelocity, setSourceVelocity] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [showGraph, setShowGraph] = useState(false);

  const calculateDoppler = () => {
    setError('');
    
    // Validate inputs
    const f0 = parseFloat(sourceFrequency);
    const vObs = parseFloat(observerVelocity);
    const vSrc = parseFloat(sourceVelocity);
    const c = 299792458; // Speed of light in m/s

    if (isNaN(f0) || f0 <= 0) {
      setError('Source frequency must be a positive number');
      return;
    }

    if (isNaN(vObs)) {
      setError('Observer velocity must be a number');
      return;
    }

    if (isNaN(vSrc)) {
      setError('Source velocity must be a number');
      return;
    }

    // Calculate classical Doppler effect
    const classicalDoppler = f0 * ((c + vObs) / (c + vSrc));

    // Calculate relativistic Doppler effect
    const gammaObs = Math.sqrt(1 - (vObs * vObs) / (c * c));
    const gammaSrc = Math.sqrt(1 - (vSrc * vSrc) / (c * c));
    const relativisticDoppler = f0 * ((1 + vObs/c) * gammaObs) / ((1 + vSrc/c) * gammaSrc);

    // Calculate frequency shift percentages
    const classicalShift = ((classicalDoppler - f0) / f0) * 100;
    const relativisticShift = ((relativisticDoppler - f0) / f0) * 100;

    const result = {
      sourceFrequency: f0,
      classicalDoppler: classicalDoppler.toFixed(2),
      relativisticDoppler: relativisticDoppler.toFixed(2),
      classicalShift: classicalShift.toFixed(2),
      relativisticShift: relativisticShift.toFixed(2),
      observerVelocity: vObs,
      sourceVelocity: vSrc
    };

    setResult(result);
    setShowGraph(true);
  };

  const generateGraphData = () => {
    if (!result) return null;

    // Generate data for frequency shift comparison
    const series = [
      {
        name: 'Classical Doppler',
        data: Array.from({ length: 100 }, (_, i) => {
          const velocity = -0.99 + (i * 0.02); // Range from -0.99c to 0.99c
          const classicalShift = (result.sourceFrequency * ((1 + velocity) / (1 - velocity)) - result.sourceFrequency) / result.sourceFrequency * 100;
          return { x: velocity, y: classicalShift };
        })
      },
      {
        name: 'Relativistic Doppler',
        data: Array.from({ length: 100 }, (_, i) => {
          const velocity = -0.99 + (i * 0.02); // Range from -0.99c to 0.99c
          const gamma = Math.sqrt(1 - velocity * velocity);
          const relativisticShift = (result.sourceFrequency * ((1 + velocity) * gamma) - result.sourceFrequency) / result.sourceFrequency * 100;
          return { x: velocity, y: relativisticShift };
        })
      }
    ];

    const options = {
      chart: {
        type: 'line',
        height: 400,
        zoom: {
          enabled: false,
        },
        toolbar: {
          show: false
        }
      },
      stroke: {
        width: 2,
        curve: 'smooth'
      },
      xaxis: {
        title: {
          text: 'Velocity (c)',
        },
        min: -1,
        max: 1,
        labels: {
          formatter: function (value) {
            return value.toFixed(2);
          }
        }
      },
      yaxis: {
        title: {
          text: 'Frequency Shift (%)',
        },
        labels: {
          formatter: function (value) {
            return value.toFixed(2);
          }
        }
      },
      title: {
        text: 'Classical vs Relativistic Doppler Effect',
        align: 'center',
      },
      colors: ['#4F46E5', '#10B981'],
      annotations: {
        yaxis: [{
          y: 0,
          borderColor: '#E5E7EB',
          label: {
            show: true,
            text: 'No Shift',
            style: {
              color: '#4B5563',
              background: '#fff',
            },
          }
        }]
      }
    };

    return { series, options };
  };

  return (
    <div className="min-h-screen bg-gradient-to-r from-blue-100 to-cyan-100">
      <Helmet>
        <title>Relativistic Doppler Effect Calculator | Physics Tool</title>
        <meta name="description" content="Calculate the relativistic Doppler effect for light sources. Interactive calculator showing the difference between classical and relativistic frequency shifts." />
        <meta name="keywords" content="relativistic doppler effect calculator, physics calculator, light frequency calculator, special relativity calculator, physics tool" />
        <meta property="og:title" content="Relativistic Doppler Effect Calculator | Physics Tool" />
        <meta property="og:description" content="Calculate the relativistic Doppler effect for light sources. Interactive calculator showing the difference between classical and relativistic frequency shifts." />
        <meta property="og:image" content="/social-preview.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Relativistic Doppler Effect Calculator | Physics Tool" />
        <meta name="twitter:description" content="Calculate the relativistic Doppler effect for light sources. Interactive calculator showing the difference between classical and relativistic frequency shifts." />
        <link rel="canonical" href="https://yourdomain.com/physics-calculators/relativistic-doppler" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <div className="header-icon text-4xl text-blue-600 mb-4">
            <FaSearch />
          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Relativistic Doppler Effect Calculator</h1>
          <p className="text-xl text-gray-600">Calculate Frequency Shifts in Relativistic Motion</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold mb-4">Calculator</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Source Frequency (Hz)</label>
                <input
                  type="number"
                  value={sourceFrequency}
                  onChange={(e) => setSourceFrequency(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter source frequency"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Observer Velocity (m/s)</label>
                <input
                  type="number"
                  value={observerVelocity}
                  onChange={(e) => setObserverVelocity(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter observer velocity"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Source Velocity (m/s)</label>
                <input
                  type="number"
                  value={sourceVelocity}
                  onChange={(e) => setSourceVelocity(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter source velocity"
                />
              </div>
              <div>
                <button
                  onClick={calculateDoppler}
                  className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                >
                  Calculate Doppler Effect
                </button>
              </div>

              {error && (
                <div className="mt-4 p-4 bg-red-100 rounded">
                  <p className="text-red-800">{error}</p>
                </div>
              )}

              {result && (
                <div className="mt-4 p-4 bg-blue-100 rounded">
                  <div className="flex items-center">
                    <FaCalculator className="text-2xl text-blue-600 mr-2" />
                    <div>
                      <h3 className="text-lg font-bold text-blue-800">Results</h3>
                      <div className="mt-2 space-y-2">
                        <p className="text-blue-800">Source Frequency: {result.sourceFrequency} Hz</p>
                        <p className="text-blue-800">Classical Doppler Effect: {result.classicalDoppler} Hz</p>
                        <p className="text-blue-800">Relativistic Doppler Effect: {result.relativisticDoppler} Hz</p>
                        <p className="text-blue-800">Classical Shift: {result.classicalShift}%</p>
                        <p className="text-blue-800">Relativistic Shift: {result.relativisticShift}%</p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 p-4 bg-white rounded-lg shadow-md">
                    <div className="flex items-center mb-4">
                      <FaLightbulb className="text-blue-500 mr-3" />
                      <h3 className="text-lg font-semibold">Solution Explanation</h3>
                    </div>
                    <p className="text-gray-600">
                      Using the relativistic Doppler effect formula:
                      <br />
                      <br />
                      f = f0 * [(1 + v/c) * gamma] / [(1 + v/c) * gamma]
                      <br />
                      <br />
                      Where:
                      <br />
                      f0 = Source frequency ({result.sourceFrequency} Hz)
                      <br />
                      v = Relative velocity
                      <br />
                      c = Speed of light (299,792,458 m/s)
                      <br />
                      gamma = Lorentz factor (sqrt(1 - v²/c²))
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {showGraph && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-bold mb-4">Frequency Shift Comparison</h2>
              <Chart
                options={generateGraphData().options}
                series={generateGraphData().series}
                type="line"
                height={400}
              />
              <p className="mt-4 text-gray-600 text-center">
                Shows the difference between classical and relativistic Doppler effects
              </p>
            </div>
          )}
        </div>

        <div className="mt-12 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">How to Use</h2>
          <ol className="list-decimal list-inside space-y-2">
            <li>Enter the source frequency in Hz</li>
            <li>Enter the observer's velocity in m/s (positive for approaching)</li>
            <li>Enter the source's velocity in m/s (positive for approaching)</li>
            <li>Click "Calculate Doppler Effect" to get the results</li>
            <li>View the frequency shifts and comparison graph</li>
          </ol>
        </div>

        <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">Features & Benefits</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="text-xl font-semibold mb-2">Relativistic Calculations</h3>
              <p>Includes Lorentz factor for accurate results</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Interactive Visualization</h3>
              <p>Compare classical and relativistic effects</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Educational Tool</h3>
              <p>Ideal for students learning special relativity</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Instant Feedback</h3>
              <p>Real-time validation and error messages for incorrect inputs</p>
            </div>
          </div>
        </div>

        <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">FAQs</h2>
          <div className="space-y-6">
            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What is the Doppler Effect?</h3>
              <p className="text-gray-600">
                The Doppler effect is the change in frequency or wavelength of a wave in relation to an observer moving relative to the wave source.
              </p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">Why is relativistic Doppler different?</h3>
              <p className="text-gray-600">
                Relativistic Doppler effect accounts for time dilation and length contraction at high velocities, leading to different results than classical Doppler.
              </p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What units should I use?</h3>
              <p className="text-gray-600">
                Use Hz for frequency and m/s for velocities. The calculator automatically handles the speed of light (c).
              </p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">Why is this calculator useful?</h3>
              <p className="text-gray-600">
                This calculator helps:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Students understand special relativity concepts</li>
                  <li>Researchers analyze high-speed motion effects</li>
                  <li>Teachers demonstrate relativistic phenomena</li>
                  <li>Anyone interested in modern physics</li>
                </ul>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RelativisticDopplerCalculator;
